import { Box, Button, Divider, Group, NumberInput, Select, SimpleGrid, Stack, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import type { AdPlatform } from '@marketing-tool/shared';
import dayjs from 'dayjs';

import { preventEnterSubmit } from '../../../lib/utils';

export type CreateCampaignFormData = {
    platform: AdPlatform;
    name: string;
    startDate: string;
    endDate: string;
    optimizationType?: 'VISIT_MY_WEBSITE' | 'WEBSITE_CONVERSION';
    spendingLimitType?: 'NONE' | 'MONTHLY' | 'LIFETIME';
    /** Budget limit in micro-currency units (1/1,000,000 of the account currency, e.g., JPY) */
    spendingLimitMicro?: number;
};

type CreateCampaignFormProps = {
    platform: AdPlatform;
    onSubmit: (data: CreateCampaignFormData) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
    initialData?: Partial<CreateCampaignFormData>;
};

export const CreateCampaignForm = ({
    platform,
    onSubmit,
    onCancel,
    isSubmitting,
    initialData,
}: CreateCampaignFormProps) => {
    const form = useForm<CreateCampaignFormData>({
        initialValues: {
            platform,
            name: initialData?.name || '',
            startDate: initialData?.startDate || dayjs().format('YYYY-MM-DD'),
            endDate: initialData?.endDate || dayjs().add(30, 'days').format('YYYY-MM-DD'),
            optimizationType: initialData?.optimizationType || 'VISIT_MY_WEBSITE',
            spendingLimitType: initialData?.spendingLimitType || 'NONE',
            spendingLimitMicro: initialData?.spendingLimitMicro,
        },
        validate: {
            endDate: (value, values) => {
                if (value && values.startDate && dayjs(value).isBefore(dayjs(values.startDate))) {
                    return 'End date must be after start date';
                }
                return null;
            },
        },
    });

    const handleSubmit = form.onSubmit((values) => {
        onSubmit(values);
    });

    return (
        <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <Stack gap="lg">
                <TextInput
                    label="Campaign Name"
                    placeholder="Enter campaign name"
                    required
                    disabled={isSubmitting}
                    {...form.getInputProps('name')}
                />

                <Select
                    label="Optimization Type"
                    required
                    data={[
                        { value: 'VISIT_MY_WEBSITE', label: 'Visit My Website' },
                        { value: 'WEBSITE_CONVERSION', label: 'Website Conversion' },
                    ]}
                    description={
                        form.values.optimizationType === 'VISIT_MY_WEBSITE'
                            ? 'Optimize for clicks to your website'
                            : 'Optimize for conversion events on your website'
                    }
                    disabled={isSubmitting}
                    value={form.values.optimizationType}
                    onChange={(val) => {
                        if (val !== null)
                            form.setFieldValue('optimizationType', val as CreateCampaignFormData['optimizationType']);
                    }}
                />

                <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md">
                    <DatePickerInput
                        label="Start Date"
                        value={form.values.startDate ? dayjs(form.values.startDate).toDate() : null}
                        onChange={(date) => {
                            form.setFieldValue('startDate', date ? dayjs(date).format('YYYY-MM-DD') : '');
                        }}
                        disabled={isSubmitting}
                        required
                        error={form.errors.startDate}
                    />

                    <DatePickerInput
                        label="End Date"
                        value={form.values.endDate ? dayjs(form.values.endDate).toDate() : null}
                        onChange={(date) => {
                            form.setFieldValue('endDate', date ? dayjs(date).format('YYYY-MM-DD') : '');
                        }}
                        disabled={isSubmitting}
                        required
                        error={form.errors.endDate}
                    />
                </SimpleGrid>

                <Select
                    label="Spending Limit Type"
                    required
                    data={[
                        { value: 'NONE', label: 'No Spending Limit' },
                        { value: 'MONTHLY', label: 'Monthly Budget' },
                        { value: 'LIFETIME', label: 'Lifetime Budget' },
                    ]}
                    description={
                        form.values.spendingLimitType === 'NONE'
                            ? 'Campaign will run without a spending limit'
                            : form.values.spendingLimitType === 'MONTHLY'
                              ? 'Set a monthly spending limit for this campaign'
                              : 'Set a total lifetime spending limit for this campaign'
                    }
                    disabled={isSubmitting}
                    value={form.values.spendingLimitType}
                    onChange={(val) => {
                        if (val !== null)
                            form.setFieldValue('spendingLimitType', val as CreateCampaignFormData['spendingLimitType']);
                    }}
                />

                {form.values.spendingLimitType !== 'NONE' && (
                    <Box>
                        <NumberInput
                            label="Spending Limit (JPY)"
                            min={0}
                            step={1}
                            value={form.values.spendingLimitMicro ? form.values.spendingLimitMicro / 1_000_000 : ''}
                            onChange={(val) => {
                                const value = typeof val === 'number' ? val : parseFloat(String(val));
                                form.setFieldValue('spendingLimitMicro', isNaN(value) ? undefined : value * 1_000_000);
                            }}
                            placeholder="Enter spending limit in JPY"
                            required
                            disabled={isSubmitting}
                            description="Enter the spending limit in Japanese Yen. Campaign will pause automatically when this limit is reached."
                        />
                    </Box>
                )}

                <Divider />
                <Group justify="flex-end" gap="sm">
                    <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Creating...' : 'Create Campaign'}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
